import { MeasureCardRow } from 'components/MeasureCardRow';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { PageContainer, PageSectionTitle, Tile } from 'components/Layout/PageContainer';
import { useIntl } from 'react-intl';
import { useEffect } from 'react';
import { toFeesAndCostsRateMeasure, useProfitabilityMeasures } from 'components/profitability/profitabilityMeasures';
import { ProfitabilityWaterfallChart } from 'components/profitability/ProfitabilityWaterfallChart';
import { ProfitabilityHubInfoContent } from 'components/profitability/ProfitabilityHubInfoContent';
import { RankedProfitabilityMovers } from 'components/profitability/RankedProfitabilityMovers';
import { InvestigateProfitabilitySection } from './components/InvestigateProfitabilitySection';
import { useProfitabilityOverviewKpis } from 'components/profitability/queryHooks';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { useDataRequest } from 'hooks/useDataRequest';
import { useDatePickerLabels } from 'hooks/useDatePickerLabels';

export function OverviewPage() {
  const intl = useIntl();
  const { setContent } = useInfoDrawer();

  useEffect(() => {
    setContent({
      titleKey: 'entities.lens.profitability',
      content: <ProfitabilityHubInfoContent />,
    });
    return () => setContent(null);
  }, [setContent]);

  const [netSoldGmv, cm1Rate, slsFeeRate, cm2Rate, netSlsLogCostRate] = useProfitabilityMeasures();
  const measures = [netSoldGmv, cm1Rate, toFeesAndCostsRateMeasure(slsFeeRate, netSlsLogCostRate), cm2Rate];

  const requestData = useDataRequest();

  const { data: totalsData, isPending, isError, error } = useProfitabilityOverviewKpis(requestData);

  const datePickerLabels = useDatePickerLabels();

  const getTileByLoadingStatus = () => {
    switch (true) {
      case isError:
        return <Tile errorDetails={error?.message ?? null} testId="profitability-kpis-error" />;
      case isPending || !totalsData:
        return (
          <Tile>
            <MetricsLoadingSpinner />
          </Tile>
        );
      default:
        return (
          <>
            <MeasureCardRow
              measures={measures}
              totalsData={totalsData}
              customComparisonPeriod={intl.formatMessage({
                id: 'datepicker.options.comparison.previous.year.week',
              })}
              testid="profitability-measure-card-row"
            />
            <ProfitabilityWaterfallChart
              data={totalsData}
              comparisonPeriod="datepicker.options.comparison.previous.year.week"
            />
          </>
        );
    }
  };

  return (
    <div className="px-10 max-w-none">
      <PageContainer>
        <PageSectionTitle
          titleId="entities.lens.profitability"
          additionalTitleId={datePickerLabels.dateRangeLabel ?? undefined}
          subtitle={intl.formatMessage({ id: 'profitability.overview.subtitle' })}
        />
        {getTileByLoadingStatus()}
        <RankedProfitabilityMovers requestData={requestData} />
        <PageSectionTitle
          titleId="profitability.deepDive.title"
          subtitle={intl.formatMessage({ id: 'profitability.deepDive.subtitle' })}
        />
        <InvestigateProfitabilitySection />
      </PageContainer>
    </div>
  );
}
